import { fetchEventSource } from '@microsoft/fetch-event-source';

// 自定义致命错误类，用于不重试的错误
class FatalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FatalError';
  }
}

export interface SSEOptions {
  timeout?: number;
  headers?: Record<string, string>;
  retryAttempts?: number;
  retryDelay?: number;
}

export interface SSEHandlers {
  onMessage?: (data: any) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
  onOpen?: () => void;
}

export class SSEClient {
  private abortController: AbortController | null = null;
  private timeoutId: NodeJS.Timeout | null = null;

  async connect(url: string, data?: any, handlers: SSEHandlers = {}, options: SSEOptions = {}) {
    const {
      timeout = 0, // 默认无超时限制
      headers = {},
    } = options;

    this.abortController = new AbortController();

    // 设置超时
    if (timeout > 0) {
      this.timeoutId = setTimeout(() => {
        this.abort();
        handlers.onError?.(new Error(`请求超时，超过 ${timeout / 1000} 秒`));
      }, timeout);
    }

    try {
      await fetchEventSource(url, {
        method: data ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Connection': 'keep-alive',
          ...headers,
        },
        openWhenHidden: true,
        ...(data && { body: JSON.stringify(data) }),
        signal: this.abortController.signal,
        // === 新增配置 ===

        // 使用原生 fetch，避免任何包装器的超时
        fetch: fetch,

        // 连接打开时的回调
        onopen: async (response) => {
          if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
          }

          if (response.ok) {
            console.log('SSE 连接已建立');
            handlers.onOpen?.();
          } else {
            // 所有错误都不重试
            throw new FatalError(`HTTP ${response.status}: ${response.statusText}`);
          }
        },

        // 接收消息时的回调
        onmessage: (event) => {
          try {
            // 重置超时计时器（如果设置了的话）
            if (timeout > 0 && !this.timeoutId) {
              this.timeoutId = setTimeout(() => {
                this.abort();
                handlers.onError?.(new Error(`请求超时，超过 ${timeout / 1000} 秒`));
              }, timeout);
            }

            // 解析消息数据
            if (event.data && event.data !== '[DONE]') {
              const data = JSON.parse(event.data);
              handlers.onMessage?.(data);
            }
          } catch (error) {
            console.error('SSE message parsing failed:', error, 'Raw data:', event.data?.substring(0, 100));
            // 不中断连接，继续处理其他消息
          }
        },

        // 连接关闭时的回调
        onclose: () => {
          if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
          }
          console.log('SSE 连接已关闭');
          handlers.onComplete?.();
        },

        // 错误处理回调
        onerror: (error) => {
          console.error('SSE 连接错误:', error);
          // 禁用自动重连：将所有错误都作为致命错误处理
          handlers.onError?.(error);
          // 抛出 FatalError 阻止自动重连
          throw new FatalError(error.message || 'SSE连接错误');
        },
      });
    } catch (error) {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      handlers.onError?.(error as Error);
    }
  }

  abort() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  // 检查是否正在连接
  isConnected(): boolean {
    return this.abortController !== null;
  }
}

// 便捷的工厂函数
export function createSSEClient(): SSEClient {
  return new SSEClient();
}

// 专门为当前项目定制的 SSE 客户端
export function createProjectSSEClient() {
  const sseClient = createSSEClient();

  return {
    connect: (url: string, data?: any, handlers: SSEHandlers = {}, options: SSEOptions = {}) => {
      // 自动添加项目认证头
      const defaultHeaders = {
        'utoken': (() => {
          const utoken = localStorage.getItem('utoken');
          return utoken || '';
        })(),
      };

      const mergedOptions = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      };

      return sseClient.connect(url, data, handlers, mergedOptions);
    },
    abort: () => sseClient.abort(),
    isConnected: () => sseClient.isConnected(),
  };
}
